import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/shared/lib/api";
import { executeRecaptcha } from "@/shared/lib/recaptcha";

const AuthContext = createContext(null);

/** Persist the resolved role for the device so the top-level Dispatcher
 *  can render the right app shell DURING the /auth/me round-trip and
 *  never flashes the rider UI for a returning driver. Cleared on logout
 *  + on any /auth/me 401. */
const ROLE_KEY = "link.role";
function rememberRole(user) {
  try {
    if (!user) { localStorage.removeItem(ROLE_KEY); return; }
    if (user.is_driver)            localStorage.setItem(ROLE_KEY, "driver");
    else if (user.role === "admin") localStorage.setItem(ROLE_KEY, "admin");
    else                            localStorage.setItem(ROLE_KEY, "rider");
  } catch (_e) { /* private mode — non-fatal */ }
}

/**
 * Auth context. Relies exclusively on the backend's httpOnly `access_token`
 * cookie — we never persist the JWT in localStorage (that would expose it to
 * XSS). On mount we hit /auth/me; if the cookie is valid we get the user.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // C2 · If returning from Emergent OAuth callback, skip the /me
    // check — <AuthCallback> will exchange the session_id and set our
    // access_token cookie BEFORE calling refreshUser. Racing /auth/me
    // here would set user=false and leave the UI in a signed-out
    // state until manual refresh.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) { setUser(data); rememberRole(data); }
      } catch {
        if (!cancelled) { setUser(false); rememberRole(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const recaptcha_token = await executeRecaptcha("login");
    const { data } = await api.post("/auth/login", { email, password, recaptcha_token });
    setUser(data.user);
    rememberRole(data.user);
    return data.user;
  }, []);

  // Accepts either the legacy 3-arg signature (name, email, password) for the
  // older Register page, OR a single payload object for the new progressive
  // onboarding flow.  Both shapes pass through the same /auth/register call
  // with a fresh reCAPTCHA token attached.
  const register = useCallback(async (...args) => {
    let payload;
    if (args.length === 1 && typeof args[0] === "object" && args[0] !== null) {
      payload = { ...args[0] };
    } else {
      const [name, email, password] = args;
      payload = { name, email, password };
    }
    const recaptcha_token = await executeRecaptcha("signup");
    const { data } = await api.post("/auth/register", { ...payload, recaptcha_token });
    setUser(data.user);
    rememberRole(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Surfacing logout errors is noisy; log for debugging.
      console.warn("Logout request failed:", err?.message || err);
    }
    setUser(null);
    rememberRole(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      rememberRole(data);
      return data;
    } catch (err) {
      console.warn("refreshUser failed:", err?.message || err);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, refresh: refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
